import React, { useMemo, useState } from "react";
import { CalendarClock, FileText, PenSquare, RefreshCw, Search, Trash2 } from "lucide-react";
import { BrandDetails } from "../../../types/add-brand-types";
import { WeeklyPlanItem, WeeklyPlanItemAngle } from "../../../types/weekly-plan";
import useWeeklyPlan from "../../../hooks/useWeeklyPlan";
import "./weeklyPlan.css";

interface WeeklyPlanProps {
  brandDetails: BrandDetails;
  onOpenPlanItem: (item: WeeklyPlanItem) => void;
}

const formatItemDate = (iso: string) => {
  const date = new Date(iso);
  return date.toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

const formatItemTime = (iso: string) => {
  return new Date(iso).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const STATUS_LABELS: Record<WeeklyPlanItem["status"], string> = {
  planned: "Planned",
  drafted: "Drafted",
  scheduled: "Scheduled",
  published: "Published",
  skipped: "Skipped",
};

const ANGLE_LABELS: Record<WeeklyPlanItemAngle, string> = {
  educational: "Educational",
  blog_amplification: "Blog promo",
  audience_pain: "Pain point",
  product_proof: "Product proof",
  seo_education: "SEO draft",
};

const SOCIAL_ANGLE_ORDER: WeeklyPlanItemAngle[] = [
  "educational",
  "audience_pain",
  "product_proof",
];

const isKnownAngle = (angle: string | undefined): angle is WeeklyPlanItemAngle =>
  Boolean(angle && angle in ANGLE_LABELS);

const getItemAngle = (
  item: WeeklyPlanItem,
  socialIndex: number
): WeeklyPlanItemAngle => {
  const metadataAngle =
    typeof item.metadata?.angle === "string" ? item.metadata.angle : undefined;
  if (isKnownAngle(metadataAngle)) {
    return metadataAngle;
  }
  if (item.content_type === "seo_article") {
    return "seo_education";
  }
  return SOCIAL_ANGLE_ORDER[Math.min(socialIndex, SOCIAL_ANGLE_ORDER.length - 1)];
};

const WeeklyPlan: React.FC<WeeklyPlanProps> = ({
  brandDetails,
  onOpenPlanItem,
}) => {
  const {
    weekStart,
    setWeekStart,
    weekOptions,
    plan,
    items,
    loading,
    generating,
    generatePlan,
    updateItem,
    deleteItem,
  } = useWeeklyPlan(brandDetails.id);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const progress = useMemo(() => {
    const counts = items.reduce<Record<WeeklyPlanItem["status"], number>>(
      (acc, item) => {
        acc[item.status] += 1;
        return acc;
      },
      {
        planned: 0,
        drafted: 0,
        scheduled: 0,
        published: 0,
        skipped: 0,
      }
    );
    const total = items.length;
    const movedForward =
      counts.drafted + counts.scheduled + counts.published + counts.skipped;
    const queuedOrDone = counts.scheduled + counts.published + counts.skipped;
    return {
      counts,
      total,
      actionNeeded: counts.planned + counts.drafted,
      queuedOrDone,
      percent: total ? Math.round((movedForward / total) * 100) : 0,
    };
  }, [items]);

  return (
    <div className="weekly-plan">
      <div className="weekly-plan__header">
        <div>
          <p className="weekly-plan__eyebrow">Brand plan</p>
          <h1 className="weekly-plan__title">Weekly Plan</h1>
          <p className="weekly-plan__subtitle">
            AI-suggested work for this brand. Planned items do not publish or
            draft anything until you open them.
          </p>
        </div>
        <div className="weekly-plan__actions">
          <select
            className="weekly-plan__select"
            value={weekStart}
            onChange={(event) => setWeekStart(event.target.value)}
          >
            {weekOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="weekly-plan__primary"
            onClick={generatePlan}
            disabled={generating}
          >
            <RefreshCw size={16} className={generating ? "weekly-plan__spin" : ""} />
            {plan ? "Regenerate" : "Generate plan"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="weekly-plan__empty">Loading weekly plan...</div>
      ) : items.length === 0 ? (
        <div className="weekly-plan__empty">
          <CalendarClock size={34} />
          <h2>No plan for this week</h2>
          <p>Generate a plan to get 3 social tasks and 1 SEO draft task.</p>
        </div>
      ) : (
        <>
          <section className="weekly-plan__progress" aria-label="Weekly plan progress">
            <div>
              <p className="weekly-plan__progress-label">
                {progress.queuedOrDone} queued or published
              </p>
              <p className="weekly-plan__progress-subtitle">
                {progress.actionNeeded} item(s) still need action.
              </p>
            </div>
            <div className="weekly-plan__progress-main">
              <div className="weekly-plan__progress-track">
                <div
                  className="weekly-plan__progress-fill"
                  style={{ width: `${progress.percent}%` }}
                />
              </div>
              <div className="weekly-plan__status-summary">
                {(Object.keys(STATUS_LABELS) as WeeklyPlanItem["status"][]).map(
                  (status) => (
                    <span
                      key={status}
                      className={`weekly-plan__status-chip weekly-plan__status-chip--${status}`}
                    >
                      {STATUS_LABELS[status]} {progress.counts[status]}
                    </span>
                  )
                )}
              </div>
            </div>
          </section>

          <div className="weekly-plan__list">
            {items.map((item, index) => {
              const isEditing = editingItemId === item.id;
              const socialIndex = items
                .slice(0, index + 1)
                .filter((candidate) => candidate.content_type === "social")
                .length - 1;
              const angle = getItemAngle(item, Math.max(socialIndex, 0));
              return (
                <article
                  key={item.id}
                  className={`weekly-plan__item weekly-plan__item--${item.status}`}
                >
                  <div className="weekly-plan__item-icon">
                    {item.content_type === "seo_article" ? (
                      <Search size={19} />
                    ) : (
                      <FileText size={19} />
                    )}
                  </div>
                  <div className="weekly-plan__item-main">
                    <div className="weekly-plan__item-meta">
                      <span className="weekly-plan__item-type">
                        {item.content_type === "seo_article"
                          ? "SEO article"
                          : item.platform || "Social"}
                      </span>
                      <span
                        className={`weekly-plan__angle weekly-plan__angle--${angle}`}
                      >
                        {ANGLE_LABELS[angle]}
                      </span>
                      <span>{formatItemDate(item.planned_for)}</span>
                      <span>{formatItemTime(item.planned_for)}</span>
                      {item.source_content_log_id && (
                        <span className="weekly-plan__linked-badge">
                          Linked to post
                        </span>
                      )}
                      <select
                        className="weekly-plan__status"
                        value={item.status}
                        onChange={(event) =>
                          updateItem(item.id, {
                            status: event.target.value as WeeklyPlanItem["status"],
                          })
                        }
                      >
                        <option value="planned">Planned</option>
                        <option value="drafted">Drafted</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="published">Published</option>
                        <option value="skipped">Skipped</option>
                      </select>
                    </div>
                    {isEditing ? (
                      <div className="weekly-plan__edit">
                        <input
                          defaultValue={item.topic}
                          onBlur={(event) =>
                            updateItem(item.id, { topic: event.target.value })
                          }
                        />
                        <textarea
                          defaultValue={item.brief}
                          onBlur={(event) =>
                            updateItem(item.id, { brief: event.target.value })
                          }
                        />
                        <input
                          type="datetime-local"
                          defaultValue={item.planned_for.slice(0, 16)}
                          onBlur={(event) => {
                            if (event.target.value) {
                              updateItem(item.id, {
                                planned_for: new Date(
                                  event.target.value
                                ).toISOString(),
                              });
                            }
                          }}
                        />
                      </div>
                    ) : (
                      <>
                        <h2 className="weekly-plan__item-title">{item.topic}</h2>
                        <p className="weekly-plan__item-brief">{item.brief}</p>
                      </>
                    )}
                  </div>
                  <div className="weekly-plan__item-actions">
                    <button
                      type="button"
                      className="weekly-plan__ghost"
                      onClick={() => onOpenPlanItem(item)}
                    >
                      <PenSquare size={15} />
                      Open
                    </button>
                    <button
                      type="button"
                      className="weekly-plan__icon-button"
                      onClick={() =>
                        setEditingItemId(isEditing ? null : item.id)
                      }
                      title="Edit item"
                    >
                      <PenSquare size={15} />
                    </button>
                    <button
                      type="button"
                      className="weekly-plan__icon-button weekly-plan__icon-button--danger"
                      onClick={() => deleteItem(item.id)}
                      title="Delete item"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default WeeklyPlan;
